import { Skeleton, Table } from 'antd'
import type { ColumnsType, ColumnType } from 'antd/es/table'
import React from 'react'
import styled from 'styled-components'

import { listTableStyles } from 'components/ui/list-table-styles'
import type { AgentListRow } from 'features/trend-detection/features/agent-list/components/AgentListView'

interface SkeletonRow {
  key: string
}

interface AgentsTableSkeletonProps {
  /** The live column defs — only title / width / align are read, so the
   * skeleton's header can never drift from the real table's. */
  columns: ColumnsType<AgentListRow>
  rowCount?: number
}

/**
 * Row-level placeholder for the agents table: real column chrome (header
 * labels/widths), fake shimmering cells. Rendered instead of the real table
 * while agents are loading, alongside the page's static header/button/banner
 * — never a full-page spinner.
 */
export const AgentsTableSkeleton: React.FC<AgentsTableSkeletonProps> = ({
  columns,
  rowCount = 3,
}) => {
  const skeletonColumns: ColumnsType<SkeletonRow> = columns.map((col, i) => {
    const { title, width, align } = col as ColumnType<AgentListRow>
    // Action columns (CTA + kebab) have an empty header; render no shimmer
    // in their cells so the skeleton row visually ends at "Latest findings"
    // instead of trailing two orphan shimmer blocks.
    const isActionColumn = !title
    return {
      key: `col-${i}`,
      title: title as React.ReactNode,
      width,
      align,
      render: () =>
        isActionColumn ? null : <Skeleton.Input active size="small" block />,
    }
  })

  const skeletonRows: SkeletonRow[] = Array.from(
    { length: rowCount },
    (_, i) => ({ key: `skeleton-${i}` }),
  )

  return (
    <StyledSkeletonTable
      data-testid="agents-table-skeleton"
      columns={skeletonColumns}
      dataSource={skeletonRows}
      rowKey="key"
      pagination={false}
    />
  )
}

const StyledSkeletonTable = styled(Table<SkeletonRow>)`
  ${listTableStyles}
`
